import React from 'react';
import cloudlinkImg      from '../../assets/images/Project/cloudlink.jpeg';
import netifyImg         from '../../assets/images/Project/netify.jpeg';
import fixbuildImg       from '../../assets/images/Project/fixbuildcompany.png';
import portfolioImg      from '../../assets/images/Project/tanbirportfolio.png';

const projects = [
   {
      id: 1,
      title: 'CloudLink V2Ray',
      desc: 'Multi-protocol VPN app with strong V2Ray support. C++ security layer. Server-side security included.',
      tags: ['WireGuard', 'V2Ray', 'OpenVPN', 'SSH', 'DNSTT', 'UDP'],
      stack: ['Java', 'Kotlin', 'C++', 'C', 'Go'],
      gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
      status: 'Active',
      type: 'app',
      screenshot: cloudlinkImg,
   },
   {
      id: 2,
      title: 'Netify V2Ray',
      desc: 'V2Ray-focused VPN app with wide protocol coverage. C++ security layer included.',
      tags: ['WireGuard', 'V2Ray', 'OpenVPN', 'SSH', 'DNSTT', 'UDP'],
      stack: ['Java', 'Kotlin', 'C++', 'C', 'Go'],
      gradient: 'from-amber-500 via-orange-500 to-rose-500',
      status: 'Active',
      type: 'app',
      screenshot: netifyImg,
   },
   {
      id: 3,
      title: 'FixBuild Core X Website',
      desc: 'Company website for FixBuild Core X, showcasing services, apps, and team.',
      tags: ['React', 'Tailwind CSS', 'Node.js'],
      stack: ['React', 'Tailwind CSS', 'Node.js'],
      gradient: 'from-brand-500 via-emerald-500 to-teal-500',
      status: 'Live',
      type: 'web',
      screenshot: fixbuildImg,
   },
   {
      id: 4,
      title: 'Personal Portfolio Website',
      desc: 'Personal developer portfolio, showing all personal details, projects, and skills.',
      tags: ['React', 'Tailwind CSS', 'Node.js'],
      stack: ['React', 'Tailwind CSS', 'Node.js'],
      gradient: 'from-violet-500 via-indigo-500 to-blue-500',
      status: 'Live',
      type: 'web',
      screenshot: portfolioImg,
   },
];

/* Unified tag color map */
const TAG_COLORS = {
   WireGuard:      'bg-emerald-50  dark:bg-emerald-900/30 text-emerald-700  dark:text-emerald-300',
   V2Ray:          'bg-violet-50   dark:bg-violet-900/30  text-violet-700   dark:text-violet-300',
   OpenVPN:        'bg-amber-50    dark:bg-amber-900/30   text-amber-700    dark:text-amber-300',
   SSH:            'bg-sky-50      dark:bg-sky-900/30     text-sky-700      dark:text-sky-300',
   DNSTT:          'bg-pink-50     dark:bg-pink-900/30    text-pink-700     dark:text-pink-300',
   UDP:            'bg-zinc-100    dark:bg-zinc-800       text-zinc-600     dark:text-zinc-300',
   React:          'bg-cyan-50     dark:bg-cyan-900/30    text-cyan-700     dark:text-cyan-300',
   'Tailwind CSS': 'bg-sky-50      dark:bg-sky-900/30     text-sky-700      dark:text-sky-300',
   'Node.js':      'bg-green-50    dark:bg-green-900/30   text-green-700    dark:text-green-300',
};

const STACK_COLORS = {
   Kotlin:         'bg-orange-50  dark:bg-orange-900/30 text-orange-700  dark:text-orange-300',
   Java:           'bg-red-50     dark:bg-red-900/30    text-red-700     dark:text-red-300',
   'C++':          'bg-blue-50    dark:bg-blue-900/30   text-blue-700    dark:text-blue-300',
   C:              'bg-indigo-50  dark:bg-indigo-900/30 text-indigo-700  dark:text-indigo-300',
   Go:             'bg-cyan-50    dark:bg-cyan-900/30   text-cyan-700    dark:text-cyan-300',
   React:          'bg-cyan-50    dark:bg-cyan-900/30   text-cyan-700    dark:text-cyan-300',
   'Tailwind CSS': 'bg-sky-50     dark:bg-sky-900/30    text-sky-700     dark:text-sky-300',
   'Node.js':      'bg-green-50   dark:bg-green-900/30  text-green-700   dark:text-green-300',
};

const Chip = ({ label, colorClass }) => (
   <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono font-medium ${colorClass}`}>
      {label}
   </span>
);

const ProjectsSection = () => {
   return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
         {projects.map((p) => (
            <article key={p.id} className="glass-card hover-card overflow-hidden group flex flex-col">

               {/* Top gradient strip */}
               <div className={`h-1 w-full bg-gradient-to-r ${p.gradient}`} />

               {/* Card body: phone/browser mockup + info side by side */}
               <div className="flex gap-0 h-[240px]">

                  {/* Left — mockup panel */}
                  <div className="relative shrink-0 w-[110px] flex items-end justify-center">
                     {/* Gradient bg capped at card content height */}
                     <div className={`absolute inset-0 bg-gradient-to-b ${p.gradient}`}>
                        <div className="absolute inset-0 opacity-30"
                           style={{ backgroundImage: 'radial-gradient(circle at 50% 20%, rgba(255,255,255,0.5) 0, transparent 60%)' }}
                        />
                     </div>
                     {/* Status badge */}
                     <span className="absolute top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-black/30 px-2 py-0.5 text-[9px] font-semibold text-white backdrop-blur whitespace-nowrap z-20">
                        {p.status}
                     </span>

                     {p.type === 'app' ? (
                        /* Phone mockup — sits centered, 18px below bottom edge, rises on hover */
                        <div className="relative z-10 w-[92px] h-[185px] rounded-[16px] bg-zinc-900 ring-[2.5px] ring-white/40 shadow-[0_12px_32px_rgba(0,0,0,0.5)] overflow-hidden translate-y-[18px] group-hover:translate-y-0 transition-transform duration-500">
                           <img src={p.screenshot} alt={p.title} className="h-full w-full object-cover object-top" />
                        </div>
                     ) : (
                        /* Browser mockup — fills the full panel */
                        <div className="absolute inset-0 z-10 flex flex-col overflow-hidden">
                           {/* Browser chrome bar */}
                           <div className="h-[14px] shrink-0 bg-zinc-900/80 flex items-center px-2 gap-1">
                              <span className="h-[6px] w-[6px] rounded-full bg-red-400/80" />
                              <span className="h-[6px] w-[6px] rounded-full bg-yellow-400/80" />
                              <span className="h-[6px] w-[6px] rounded-full bg-green-400/80" />
                           </div>
                           {/* Screenshot fills remaining space */}
                           <img
                              src={p.screenshot}
                              alt={p.title}
                              className="flex-1 w-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                           />
                        </div>
                     )}
                  </div>

                  {/* Right — info */}
                  <div className="flex flex-col gap-2 flex-1 p-3 min-h-0 overflow-hidden">
                     <div className="shrink-0">
                        <h3 className="font-display text-[13px] font-bold tracking-tight leading-snug">{p.title}</h3>
                        <p className="mt-0.5 text-[10.5px] leading-relaxed text-zinc-500 dark:text-zinc-400">{p.desc}</p>
                     </div>

                     <div className="flex flex-col gap-1.5 shrink-0">
                        {/* Stack */}
                        <div>
                           <p className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-1">Stack</p>
                           <div className="flex flex-wrap gap-1">
                              {p.stack.map((t) => (
                                 <Chip key={t} label={t} colorClass={STACK_COLORS[t] ?? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300'} />
                              ))}
                           </div>
                        </div>
                        {/* Tags */}
                        {p.type === 'app' && (
                           <div>
                              <p className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-1">Protocols</p>
                              <div className="flex flex-wrap gap-1">
                                 {p.tags.map((t) => (
                                    <Chip key={t} label={t} colorClass={TAG_COLORS[t] ?? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300'} />
                                 ))}
                              </div>
                           </div>
                        )}
                     </div>
                  </div>

               </div>
            </article>
         ))}
      </div>
   );
};

export default ProjectsSection;
